/**
 * BrandKitPanel.jsx — one look, every export.
 *
 * Saved on the account (not the project), so a logo and colour set chosen once
 * shows up on every video from then on. Sits next to Video export because
 * that's the only place it has any effect.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import * as L from "lucide-react";

const SWATCHES = ["#E89A3C", "#178677", "#C2410C", "#2563EB", "#7C3AED", "#DC2626", "#0F172A", "#F5EDE0"];

export function BrandKitPanel({ brand, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(brand || null);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setDraft(brand || null); }, [brand]);

  if (!draft) return null;

  const dirty = JSON.stringify(draft) !== JSON.stringify(brand);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/studio/brand-kit", {
        accent: draft.accent || "#E89A3C",
        caption_color: draft.caption_color || null,
        handle: draft.handle || "rammy.ai",
        wordmark: draft.wordmark || null,
        show_rammy_mark: draft.show_rammy_mark !== false,
      });
      onChange(data);
      toast.success("Brand kit saved — every export uses it now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the brand kit");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logos must be under 2 MB"); return; }
    setLogoBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/studio/brand-kit/logo", form, { timeout: 120000 });
      onChange(data);
      toast.success("Logo saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload that logo");
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      const { data } = await api.delete("/studio/brand-kit/logo");
      onChange(data);
      toast.success("Logo removed");
    } catch (_) {
      toast.error("Couldn't remove the logo");
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4" data-testid="studio-brand-kit">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="studio-brand-toggle"
      >
        <L.Palette size={14} className="text-[var(--rammy-cyan-dark)]"/>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text)]">Brand kit</div>
        <span className="flex items-center gap-1.5 text-[11px] text-[var(--rammy-text-muted)] min-w-0">
          <span className="inline-block w-3 h-3 rounded-full border border-black/10" style={{ background: brand.accent }}/>
          <span className="truncate">{brand.has_logo ? "Logo set" : "No logo"} · {brand.handle}</span>
        </span>
        <L.ChevronDown size={14} className={`ml-auto text-[var(--rammy-text-muted)] transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-xl border border-[var(--rammy-border)] bg-[var(--rammy-surface-alt)] flex items-center justify-center overflow-hidden shrink-0">
              {brand.has_logo
                ? <img src={`${api.defaults.baseURL}/studio/brand-kit/logo`} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="studio-brand-logo-preview"/>
                : <L.ImageOff size={18} className="text-[var(--rammy-text-muted)]"/>}
            </div>
            <div className="min-w-0">
              <div className="text-[12.5px] font-medium">Logo</div>
              <div className="text-[10.5px] text-[var(--rammy-text-muted)] mb-1.5">
                Replaces the wordmark in the top corner. PNG with transparency looks best.
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={(e) => uploadLogo(e.target.files)}
                  data-testid="studio-brand-logo-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={logoBusy}
                  className="btn-pill h-8 px-3 text-[11px] bg-[var(--rm-surface)] border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan-dark)] inline-flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="studio-brand-logo-upload"
                >
                  {logoBusy ? <L.Loader2 size={12} className="animate-spin"/> : <L.ImagePlus size={12}/>}
                  {brand.has_logo ? "Replace" : "Upload"}
                </button>
                {brand.has_logo && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    disabled={logoBusy}
                    className="text-[11px] text-[var(--rammy-text-muted)] hover:text-red-500 transition-colors"
                    data-testid="studio-brand-logo-remove"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Accent */}
          <div>
            <div className="text-[11px] text-[var(--rammy-text-muted)] mb-2">Accent — waveform and progress bar</div>
            <div className="flex items-center gap-2 flex-wrap">
              {SWATCHES.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setDraft(d => ({ ...d, accent: c }))}
                  data-active={draft.accent?.toLowerCase() === c.toLowerCase() ? "true" : "false"}
                  data-testid={`studio-brand-accent-${c.replace("#", "")}`}
                  className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                    draft.accent?.toLowerCase() === c.toLowerCase() ? "border-[var(--rammy-text)]" : "border-black/10"
                  }`}
                  style={{ background: c }}
                  aria-label={`Accent ${c}`}
                />
              ))}
              <Input
                value={draft.accent || ""}
                onChange={(e) => setDraft(d => ({ ...d, accent: e.target.value }))}
                className="rounded-full h-8 w-28 text-[11px] font-mono border-[var(--rammy-border)]"
                maxLength={9}
                data-testid="studio-brand-accent-hex"
              />
            </div>
          </div>

          {/* Text */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] text-[var(--rammy-text-muted)] mb-1.5">Wordmark (used when there's no logo)</div>
              <Input
                value={draft.wordmark || ""}
                onChange={(e) => setDraft(d => ({ ...d, wordmark: e.target.value }))}
                placeholder="Rammy Studio"
                maxLength={40}
                className="rounded-xl h-9 text-sm border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
                data-testid="studio-brand-wordmark"
              />
            </div>
            <div>
              <div className="text-[11px] text-[var(--rammy-text-muted)] mb-1.5">Footer handle</div>
              <Input
                value={draft.handle || ""}
                onChange={(e) => setDraft(d => ({ ...d, handle: e.target.value }))}
                placeholder="rammy.ai"
                maxLength={40}
                className="rounded-xl h-9 text-sm border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
                data-testid="studio-brand-handle"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-[12px] text-[var(--rammy-text-sec)] cursor-pointer">
              <input
                type="checkbox"
                checked={draft.show_rammy_mark !== false}
                onChange={(e) => setDraft(d => ({ ...d, show_rammy_mark: e.target.checked }))}
                className="accent-[var(--rammy-cyan-dark)]"
                data-testid="studio-brand-rammy-mark"
              />
              Keep a small "made with Rammy" mark
            </label>
            <Button
              onClick={save}
              disabled={!dirty || saving}
              className="btn-pill h-8 text-[11px] disabled:opacity-40"
              data-testid="studio-brand-save"
            >
              {saving ? <L.Loader2 size={12} className="animate-spin"/> : <L.Save size={12}/>} Save brand kit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
